import { Paper, styled, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { type Control, useWatch } from 'react-hook-form'

import { Box } from '@/components/ui'
import { TIMEZONE_OPTIONS } from '@/definitions/location'
import { spacing } from '@/themes/tokens'
import { formatPhoneNumber } from '@/utils/phoneUtils'
import {
  formatTaxRateFractionAsPercent,
  taxRatePercentToFraction,
} from '@/utils/taxRateUtils'

import {
  type CreateOrgFormValues,
  TAX_TYPE_OPTIONS,
} from '../definitions/createOrganizationForm'

interface ReviewStepProps {
  /**
   * Whether the organization turned out to already have staff, so this run
   * created no admin and the details collected for one were never saved.
   */
  adminAlreadyExists: boolean
  control: Control<CreateOrgFormValues>
  /**
   * Whether the location predates this run, so its fields are not this run's to
   * report.
   */
  locationOnRecord: boolean
  /** Whether the organization itself predates this run, as a resumed setup's does. */
  organizationOnRecord: boolean
  /**
   * The location the admin was assigned, or null when there was none to assign.
   * Reported rather than re-derived here so this cannot claim an assignment the
   * request did not make.
   */
  primaryLocationName: string | null
}

const ReviewPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(spacing.md),
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(spacing.sm),
}))

interface ReviewRowProps {
  label: string
  value?: string
}

const ReviewRow = ({ label, value }: ReviewRowProps) => (
  <Box variant='flexBetween'>
    <Typography variant='body2' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body2'>{value?.trim() ? value : '—'}</Typography>
  </Box>
)

interface ReviewSectionProps {
  title: string
  children: ReactNode
}

/** Joins phrases as a written list: "a", "a and b", "a, b, and c". */
const joinPhrases = (phrases: string[]): string =>
  phrases.length > 2
    ? `${phrases.slice(0, -1).join(', ')}, and ${phrases[phrases.length - 1]}`
    : phrases.join(' and ')

const ReviewSection = ({ title, children }: ReviewSectionProps) => (
  <Box variant='flexColumn' gap='sm'>
    <Typography variant='subtitle2'>{title}</Typography>
    <ReviewPaper elevation={0}>{children}</ReviewPaper>
  </Box>
)

export const ReviewStep = ({
  adminAlreadyExists,
  control,
  locationOnRecord,
  organizationOnRecord,
  primaryLocationName,
}: ReviewStepProps) => {
  const values = useWatch({ control })
  const taxTypeLabel = TAX_TYPE_OPTIONS.find(
    (option) => option.value === values.federal_tax_type
  )?.label
  const taxRateLabel = formatTaxRateFractionAsPercent(
    taxRatePercentToFraction(values.tax_rate ?? '')
  )
  const timezoneLabel = TIMEZONE_OPTIONS.find(
    (option) => option.value === values.timezone
  )?.label

  const adminName = [values.admin_name_given, values.admin_name_family]
    .filter(Boolean)
    .join(' ')

  // Named one at a time: what a run of this flow created is not the same every time.
  const possessive = organizationOnRecord ? 'the' : 'its'
  const createdRecords = [
    ...(organizationOnRecord ? [] : ['the organization']),
    ...(locationOnRecord ? [] : [`${possessive} primary location`]),
    ...(adminAlreadyExists ? [] : [`${possessive} admin user`]),
  ]
  const createdPhrase = joinPhrases(createdRecords)
  const createdSummary =
    createdRecords.length === 0
      ? 'Everything this setup creates was already on record, so nothing was created here.'
      : [
        `${createdPhrase.charAt(0).toUpperCase()}${createdPhrase.slice(1)} ${
          createdRecords.length > 1 ? 'have' : 'has'
        } been created.`,
        ...(locationOnRecord
          ? []
          : ['The practice can edit its own location from its settings.']),
      ].join(' ')

  const streetLine = [values.street, values.suite].filter(Boolean).join(', ')
  const cityLine = [
    [values.city, values.state].filter(Boolean).join(', '),
    values.postal_code,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Box variant='flexColumn' gap='md'>
      <Typography variant='body2' color='text.secondary'>
        {createdSummary}
      </Typography>
      {/* The organization and its tax details are one record, so they collapse into
          a single line when this run did not write it. */}
      {organizationOnRecord ? (
        <ReviewSection title='Organization'>
          <Typography variant='body2' color='text.secondary'>
            Already on record — the organization and its tax details were set up before
            this, and cannot be amended from here.
          </Typography>
        </ReviewSection>
      ) : (
        <>
          <ReviewSection title='Organization'>
            <ReviewRow label='Organization Name' value={values.org_name} />
            <ReviewRow label='Display Name' value={values.display_name} />
          </ReviewSection>
          <ReviewSection title='Tax'>
            <ReviewRow label='Tax ID' value={values.federal_tax_id} />
            <ReviewRow label='Tax ID Type' value={taxTypeLabel} />
            <ReviewRow label='Tax Rate' value={taxRateLabel} />
          </ReviewSection>
        </>
      )}
      <ReviewSection title='Primary Location'>
        {locationOnRecord ? (
          <Typography variant='body2' color='text.secondary'>
            {primaryLocationName
              ? `Already on record — ${primaryLocationName} is the organization's primary location.`
              : 'Already on record — this organization already had its primary location.'}
          </Typography>
        ) : (
          <>
            <ReviewRow label='Location Name' value={values.location_name} />
            <ReviewRow
              label='Phone'
              value={values.primary_phone && formatPhoneNumber(values.primary_phone)}
            />
            <ReviewRow label='Address' value={streetLine} />
            <ReviewRow label='City, State ZIP' value={cityLine} />
            <ReviewRow label='Time Zone' value={timezoneLabel} />
          </>
        )}
      </ReviewSection>
      <ReviewSection title='Admin User'>
        {adminAlreadyExists ? (
          <Typography variant='body2' color='text.secondary'>
            This organization already had an admin user, so none was created for it here.
          </Typography>
        ) : (
          <>
            <ReviewRow label='Name' value={adminName} />
            <ReviewRow label='Username' value={values.admin_username} />
            <ReviewRow label='Email' value={values.admin_email} />
            <ReviewRow label='NPI' value={values.admin_npi} />
            <ReviewRow label='License Number' value={values.admin_license_number} />
            {primaryLocationName && (
              <ReviewRow label='Primary Location' value={primaryLocationName} />
            )}
          </>
        )}
      </ReviewSection>
    </Box>
  )
}
